import type { CalendarHighlight } from '@massilia/ui'

import { useGetFeaturedFlightDatesQuery } from '@massilia/queries'
import { useMemo } from 'react'

/**
 * Jours mis en avant de `from` vers `to`, prêts pour le calendrier (#46) : vols
 * directs en bleu pour une compagnie, en rouge pour l'autre, moitié-moitié quand
 * les deux volent ; vols avec escale recensés en ambre. Rien tant que l'une des
 * deux villes manque.
 */
export function useFeaturedFlightHighlights(from?: string, to?: string): CalendarHighlight[] {
  const { data } = useGetFeaturedFlightDatesQuery(from || undefined, to || undefined)

  return useMemo(() => {
    if (!from || !to || !data) return []
    // Le serveur garde l'ordre des compagnies d'un sens à l'autre : la 1re est bleue.
    const [first, second] = data.direct
    const firstDays = new Set(first?.dates)
    const secondDays = new Set(second?.dates)
    const both = [...firstDays].filter((d) => secondDays.has(d))
    return [
      toHighlight(
        both,
        `Vols directs avec ${first?.carrierName} et ${second?.carrierName}`,
        'both'
      ),
      toHighlight(
        [...firstDays].filter((d) => !secondDays.has(d)),
        directLabel(first?.carrierName),
        'first'
      ),
      toHighlight(
        [...secondDays].filter((d) => !firstDays.has(d)),
        directLabel(second?.carrierName),
        'second'
      ),
      toHighlight(
        data.withStop.dates,
        data.withStop.carrierNames.length > 0
          ? `Vols avec 1 escale avec ${data.withStop.carrierNames.join(', ')}`
          : 'Vols avec 1 escale',
        'warning'
      ),
    ].filter((h) => h !== undefined)
  }, [data, from, to])
}

function directLabel(carrierName?: string): string {
  return carrierName ? `Vols directs avec ${carrierName}` : 'Vols directs'
}

function toHighlight(
  dates: string[],
  label: string,
  tone: CalendarHighlight['tone']
): CalendarHighlight | undefined {
  if (dates.length === 0) return undefined
  return {
    // Minuit local : react-day-picker compare des jours du calendrier, pas des instants UTC.
    dates: dates.map((iso) => {
      const [year, month, day] = iso.split('-').map(Number)
      return new Date(year, month - 1, day)
    }),
    label,
    tone,
  }
}
